function syncDrawerQtyWidth(quantityInput) {
  if (!quantityInput?.matches?.('[data-cart-drawer]')) return;
  const input = quantityInput.querySelector('.quantity__input');
  const len = String(input?.value ?? '').trim().length;
  quantityInput.classList.toggle('cart-quantity--wide', len >= 4);
}

function syncAllDrawerQtyWidths(root = document) {
  root.querySelectorAll('quantity-input[data-cart-drawer]').forEach(syncDrawerQtyWidth);
}

function handleDrawerQtyInputEvent(event) {
  const qtyInput = event.target.closest('quantity-input[data-cart-drawer]');
  if (!qtyInput) return;
  syncDrawerQtyWidth(qtyInput);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('input', handleDrawerQtyInputEvent);
    this.addEventListener('change', handleDrawerQtyInputEvent);
    this.setHeaderCartIconAccessibility();
    syncAllDrawerQtyWidths(this);
  }
  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('[data-cart-icon-bubble]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('cart-drawer-open');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'animate');
    document.body.classList.remove('cart-drawer-open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    syncCartIconBubbles();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Sync all cart icon bubbles from #cart-icon-bubble
function syncCartIconBubbles() {
  const source = document.getElementById('cart-icon-bubble');
  if (!source) return;

  const sourceBubble = source.querySelector('.cart-count-bubble');

  document.querySelectorAll('[data-cart-icon-bubble]').forEach((icon) => {
    if (icon === source) return;

    const existingBubble = icon.querySelector('.cart-count-bubble');

    if (sourceBubble) {
      if (existingBubble) {
        existingBubble.replaceWith(sourceBubble.cloneNode(true));
      } else {
        icon.appendChild(sourceBubble.cloneNode(true));
      }
    } else if (existingBubble) {
      existingBubble.remove();
    }
  });
}

function syncCartDrawerRelatedProducts(sourceInner, targetInner) {
  if (!sourceInner || !targetInner) return;

  const sourceRelated = sourceInner.querySelector('related-products-snippet');
  const targetRelated = targetInner.querySelector('related-products-snippet');
  const targetFooter = targetInner.querySelector('.drawer__footer');

  if (sourceRelated && targetRelated) {
    targetRelated.replaceWith(document.importNode(sourceRelated, true));
  } else if (sourceRelated && !targetRelated) {
    const node = document.importNode(sourceRelated, true);
    if (targetFooter) {
      targetFooter.before(node);
    } else {
      targetInner.appendChild(node);
    }
  } else if (!sourceRelated && targetRelated) {
    targetRelated.remove();
  }

  const emptyState = targetInner.querySelector('.drawer__inner-empty');
  if (emptyState) {
    emptyState.remove();
  }
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderSections(parsedState) {
    const sectionConfig = this.getSectionsToRender().find((section) => section.id === 'CartDrawer');
    const sectionHtml = sectionConfig ? parsedState.sections[sectionConfig.section] : null;

    if (sectionHtml) {
      this.replaceCartDrawerPartial(sectionHtml, parsedState.item_count);
    }

    this.getSectionsToRender()
      .filter((section) => section.id !== 'CartDrawer')
      .forEach((section) => {
        const root = document.getElementById(section.id);
        if (!root || !parsedState.sections[section.section]) return;

        const elementToReplace = root.querySelector(section.selector) || root;
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });

    syncCartIconBubbles();
  }

  replaceCartDrawerPartial(sectionHtml, itemCount) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceInner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
    const targetInner = document.getElementById('CartDrawer')?.querySelector('.drawer__inner');

    if (!sourceInner || !targetInner) return;

    if (itemCount === 0) {
      targetInner.innerHTML = sourceInner.innerHTML;
      return;
    }

    const sourceHeading = sourceInner.querySelector('.drawer__heading');
    const targetHeading = targetInner.querySelector('.drawer__heading');
    if (sourceHeading && targetHeading) {
      targetHeading.innerHTML = sourceHeading.innerHTML;
    }

    const sourceShipping = sourceInner.querySelector('free-shipping-goal');
    const targetShipping = targetInner.querySelector('free-shipping-goal');
    if (sourceShipping && targetShipping) {
      targetShipping.replaceWith(document.importNode(sourceShipping, true));
    }

    const sourceItems = sourceInner.querySelector('cart-drawer-items');
    const targetItems = targetInner.querySelector('cart-drawer-items');
    if (sourceItems && targetItems) {
      targetItems.replaceWith(document.importNode(sourceItems, true));
    }

    syncCartDrawerRelatedProducts(sourceInner, targetInner);

    const sourceFooter = sourceInner.querySelector('.drawer__footer');
    const targetFooter = targetInner.querySelector('.drawer__footer');
    if (sourceFooter && targetFooter) {
      targetFooter.replaceWith(document.importNode(sourceFooter, true));
    }

    syncAllDrawerQtyWidths(targetInner);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function restoreLiveChatIfDrawerClosed() {
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.classList.contains('active')) return;

  document.body.classList.remove('cart-drawer-open');

  if (typeof SnapEngage !== 'undefined' && typeof SnapEngage.showButton === 'function') {
    SnapEngage.showButton();
  }
}

document.addEventListener('DOMContentLoaded', restoreLiveChatIfDrawerClosed);
window.addEventListener('load', restoreLiveChatIfDrawerClosed);
